import {
  Archive,
  ArrowLeftRight,
  BookOpen,
  Building2,
  CalendarRange,
  Clock3,
  DatabaseBackup,
  Eye,
  FileArchive,
  FileText,
  Gauge,
  KeyRound,
  Library,
  type LucideIcon,
  Plug,
  Scale,
  ScrollText,
  ShieldAlert,
  Sparkles,
  SquareActivity,
  UserRoundCog,
  Users,
  UsersRound,
  Video,
  Webhook,
  Workflow,
} from 'lucide-react';

/*
 * Адміністративна частина порталу — усе, що налаштовує систему й не потрібне працівникові в щоденній роботі:
 * облікові записи й доступи, правила й автоматизація, довідники модулів, інтеграції, ШІ, дані й безпека.
 * Це не управлінський і не фінансовий контур: керівництво роботою й гроші лишаються в робочій частині
 * у своїх розділах із власними правами.
 *
 * Розділ, що належить модулю (`module`), показується лише коли модуль доступний користувачу.
 */
export type AdminItem = { to: string; label: string; icon: LucideIcon; ownerOnly?: boolean; module?: string };
export type AdminGroup = { label: string; items: AdminItem[] };

export const ADMIN_GROUPS: AdminGroup[] = [
  {
    label: 'Облікові записи й доступи',
    items: [
      { to: '/admin/users', label: 'Працівники й ролі', icon: Users },
      { to: '/admin/departments', label: 'Підрозділи', icon: Building2 },
      { to: '/admin/teams', label: 'Команди', icon: UsersRound },
      { to: '/admin/tokens', label: 'Токени API', icon: KeyRound },
    ],
  },
  {
    label: 'Правила роботи',
    items: [
      { to: '/admin/policy', label: 'Політика задач', icon: ScrollText },
      { to: '/automation', label: 'Автоматизація', icon: Workflow, module: 'automation' },
      { to: '/admin/overview', label: 'Зведення й модулі', icon: Gauge },
    ],
  },
  {
    label: 'Довідники модулів',
    items: [
      { to: '/time/settings', label: 'Облік часу', icon: Clock3, module: 'time' },
      { to: '/legal/settings', label: 'Юридичний контур', icon: Scale, module: 'legal' },
      { to: '/content/settings', label: 'Контент', icon: FileText, module: 'content' },
      { to: '/people/settings', label: 'Персонал', icon: UserRoundCog, module: 'people' },
      { to: '/kb/registry/settings', label: 'Процеси й регламенти', icon: Library, module: 'kb' },
      { to: '/meetings/settings', label: 'Зустрічі', icon: Video, module: 'meetings' },
    ],
  },
  {
    label: 'Інтеграції й ШІ',
    items: [
      { to: '/connectors', label: 'Інтеграції', icon: Plug, module: 'connectors' },
      { to: '/ai/settings', label: 'Налаштування ШІ', icon: Sparkles, module: 'ai' },
      { to: '/admin/webhooks', label: 'Вебхуки', icon: Webhook },
      { to: '/admin/calendar', label: 'Календар iCal', icon: CalendarRange },
      { to: '/admin/api', label: 'Документація API', icon: BookOpen },
    ],
  },
  {
    label: 'Дані',
    items: [
      { to: '/migration', label: 'Перенесення даних', icon: ArrowLeftRight, module: 'migration' },
      { to: '/admin/retention', label: 'Зберігання й утримання', icon: Archive },
      { to: '/admin/exports', label: 'Експорт даних', icon: FileArchive, ownerOnly: true },
      { to: '/admin/backups', label: 'Резервні копії', icon: DatabaseBackup, ownerOnly: true },
    ],
  },
  {
    label: 'Безпека',
    items: [
      { to: '/admin/audit', label: 'Журнал аудиту', icon: SquareActivity },
      { to: '/admin/views', label: 'Журнал переглядів', icon: Eye },
      { to: '/admin/alerts', label: 'Підозрілі дії', icon: ShieldAlert },
    ],
  },
];

/** Модулі, які цілком належать адміністративній частині: адміністраторові в робочому меню вони не показуються. */
export const ADMIN_MODULES = new Set(['automation', 'connectors', 'migration']);

const matches = (path: string, to: string) => path === to || path.startsWith(`${to}/`);

/** Чи належить адреса адміністративній частині (тоді бічна панель показує її розділи). */
export function isAdminPath(path: string): boolean {
  return matches(path, '/admin') || ADMIN_GROUPS.some((g) => g.items.some((i) => matches(path, i.to)));
}

export function adminGroupsFor(opts: { isOwner: boolean; hasModule: (key: string) => boolean }): AdminGroup[] {
  return ADMIN_GROUPS.map((g) => ({ ...g, items: g.items.filter((i) => (!i.ownerOnly || opts.isOwner) && (!i.module || opts.hasModule(i.module))) })).filter((g) => g.items.length > 0);
}
